(function() {
  'use strict';

  const STORAGE_PREFIX = 'vc_wishlist_customer';

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function sanitizeUrl(url) {
    const trimmed = String(url || '').trim();
    if (/^(javascript|data|vbscript)\s*:/i.test(trimmed)) return '#';
    return escapeHtml(trimmed);
  }

  function readWishlist() {
    if (window.VCWishlist && typeof window.VCWishlist.getItems === 'function') {
      return window.VCWishlist.getItems();
    }
    return [];
  }

  function getCountLabel(count) {
    return count === 1 ? '1 saved item' : `${count} saved items`;
  }

  function getFallbackStorageKey() {
    const rawCustomerId = window.vcWishlistConfig?.customerId;
    if (rawCustomerId === null || rawCustomerId === undefined) return null;

    const normalizedCustomerId = String(rawCustomerId).trim();
    if (!normalizedCustomerId || normalizedCustomerId === 'null' || normalizedCustomerId === 'undefined') {
      return null;
    }

    return `${STORAGE_PREFIX}_${normalizedCustomerId}`;
  }

  function updateCountUi() {
    const count = readWishlist().length;
    const bubbles = document.querySelectorAll('[data-wishlist-count-bubble]');

    bubbles.forEach((bubble) => {
      const visibleCount = bubble.querySelector('[data-wishlist-count]');
      const srLabel = bubble.querySelector('[data-wishlist-count-label]');

      if (count > 0) {
        bubble.hidden = false;
        if (visibleCount) visibleCount.textContent = count > 99 ? '99+' : String(count);
        if (srLabel) srLabel.textContent = getCountLabel(count);
      } else {
        bubble.hidden = true;
        if (visibleCount) visibleCount.textContent = '';
        if (srLabel) srLabel.textContent = '';
      }
    });
  }

  function buildItemMarkup(item) {
    const title = escapeHtml(item.title || 'Saved item');
    const itemUrl = sanitizeUrl(item.url || item.productUrl || '#');
    const variantTitle = escapeHtml(item.variantTitle || '');
    const price = escapeHtml(item.price || '');
    const compareAtPrice = escapeHtml(item.compareAtPrice || '');
    const image = item.image ? sanitizeUrl(item.image) : '';
    const productId = escapeHtml(item.productId || '');
    const handle = escapeHtml(item.handle || '');

    return `
      <article class="vc-wishlist-drawer__item">
        ${
          image
            ? `<a class="vc-wishlist-drawer__media" href="${itemUrl}"><img src="${image}" alt="${title}" loading="lazy"></a>`
            : '<div class="vc-wishlist-drawer__media-placeholder" aria-hidden="true"></div>'
        }
        <div class="vc-wishlist-drawer__item-content">
          <a class="vc-wishlist-drawer__item-title" href="${itemUrl}">${title}</a>
          ${variantTitle ? `<p class="vc-wishlist-drawer__item-meta">${variantTitle}</p>` : ''}
          ${
            price || compareAtPrice
              ? `<div class="vc-wishlist-drawer__item-pricing">
                  ${price ? `<span class="vc-wishlist-drawer__price">${price}</span>` : ''}
                  ${compareAtPrice ? `<span class="vc-wishlist-drawer__price vc-wishlist-drawer__price--compare">${compareAtPrice}</span>` : ''}
                </div>`
              : ''
          }
          <div class="vc-wishlist-drawer__item-actions">
            <a class="vc-wishlist-drawer__link" href="${itemUrl}">View item</a>
            <button
              type="button"
              class="vc-wishlist-drawer__remove"
              data-wishlist-remove
              data-product-id="${productId}"
              data-product-handle="${handle}"
            >
              Remove
            </button>
          </div>
        </div>
      </article>
    `;
  }

  if (!customElements.get('vc-wishlist-drawer')) {
    customElements.define(
      'vc-wishlist-drawer',
      class VCWishlistDrawer extends HTMLElement {
        constructor() {
          super();
          this.handleDocumentClick = this.handleDocumentClick.bind(this);
          this.handleKeydown = this.handleKeydown.bind(this);
          this.handleWishlistUpdate = this.handleWishlistUpdate.bind(this);
          this.handleStorageSync = this.handleStorageSync.bind(this);
        }

        connectedCallback() {
          this.overlay = this.querySelector('[data-wishlist-close-overlay]');
          this.closeButtons = this.querySelectorAll('[data-wishlist-close]');
          this.panel = this.querySelector('[data-wishlist-panel]');
          this.emptyState = this.querySelector('[data-wishlist-empty]');
          this.contentState = this.querySelector('[data-wishlist-content]');
          this.itemsContainer = this.querySelector('[data-wishlist-items]');
          this.countText = this.querySelector('[data-wishlist-count-text]');
          this.clearButton = this.querySelector('[data-wishlist-clear]');
          this.focusTarget = this.querySelector('.drawer__close') || this.querySelector('.vc-wishlist-drawer__inner');

          this.overlay?.addEventListener('click', () => this.close());
          this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
          this.clearButton?.addEventListener('click', () => {
            if (window.VCWishlist && typeof window.VCWishlist.clear === 'function') {
              window.VCWishlist.clear();
            } else {
              const storageKey = getFallbackStorageKey();
              if (storageKey) {
                localStorage.removeItem(storageKey);
              }
              window.dispatchEvent(new CustomEvent('vc:wishlist:updated', {
                detail: {
                  wishlist: [],
                  count: 0,
                  handles: [],
                  action: 'cleared',
                  productHandle: '',
                  item: null,
                },
              }));
              this.render();
              updateCountUi();
            }
          });

          document.addEventListener('click', this.handleDocumentClick);
          document.addEventListener('keydown', this.handleKeydown);
          window.addEventListener('vc:wishlist:updated', this.handleWishlistUpdate);
          window.addEventListener('storage', this.handleStorageSync);

          this.render();
          updateCountUi();
        }

        disconnectedCallback() {
          document.removeEventListener('click', this.handleDocumentClick);
          document.removeEventListener('keydown', this.handleKeydown);
          window.removeEventListener('vc:wishlist:updated', this.handleWishlistUpdate);
          window.removeEventListener('storage', this.handleStorageSync);
        }

        open(trigger) {
          this.lastTrigger = trigger || document.activeElement;
          this.classList.add('active');
          document.body.classList.add('overflow-hidden');
          window.setTimeout(() => this.focusTarget?.focus(), 10);
        }

        close() {
          this.classList.remove('active');
          document.body.classList.remove('overflow-hidden');
          this.lastTrigger?.focus?.();
        }

        render() {
          const items = readWishlist();
          const hasItems = items.length > 0;

          if (this.countText) {
            this.countText.textContent = getCountLabel(items.length);
          }

          if (this.emptyState) {
            this.emptyState.hidden = hasItems;
          }

          if (this.contentState) {
            this.contentState.hidden = !hasItems;
          }

          if (this.itemsContainer) {
            this.itemsContainer.innerHTML = hasItems ? items.map(buildItemMarkup).join('') : '';
          }
        }

        handleDocumentClick(event) {
          const openTrigger = event.target.closest('[data-wishlist-open]');
          if (openTrigger) {
            event.preventDefault();
            this.open(openTrigger);
            return;
          }

          const removeButton = event.target.closest('[data-wishlist-remove]');
          if (removeButton && this.contains(removeButton)) {
            event.preventDefault();
            if (window.VCWishlist && typeof window.VCWishlist.remove === 'function') {
              window.VCWishlist.remove(removeButton.dataset.productId, removeButton.dataset.productHandle);
            }
          }
        }

        handleKeydown(event) {
          if (event.key === 'Escape' && this.classList.contains('active')) {
            event.preventDefault();
            this.close();
          }
        }

        handleWishlistUpdate() {
          this.render();
          updateCountUi();
        }

        handleStorageSync(event) {
          if (!event.key || !event.key.startsWith(`${STORAGE_PREFIX}_`)) return;
          this.render();
          updateCountUi();
        }
      }
    );
  }

  document.addEventListener('DOMContentLoaded', updateCountUi);
})();
